// AIChatWidget.tsx - Fixed Version with Better Error Handling
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Bot, X, Minimize2, Maximize2, Send, Lightbulb, TrendingUp, AlertCircle, BarChart3, DollarSign } from 'lucide-react';
import { AccountEntry, Template, Project } from '../../types';
import { initSupabase } from '../../lib/api/supabase'; // ← ADD THIS LINE

interface AIChatWidgetProps {
  accounts: AccountEntry[];
  activeTab: string;
  activeTemplate: Template | null;
  financialData: any;
  selectedProject: Project | null;
  periodType?: 'monthly' | 'quarterly' | 'annual'; // NEW: Period type for AI analysis context
  hideWidget?: boolean;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  actions?: Array<{
    type: 'apply_categories' | 'create_chart' | 'export_data';
    label: string;
    data?: any;
  }>;
}

interface TabContextSuggestion {
  icon: React.ElementType;
  title: string;
  description: string;
  query: string;
  priority: 'high' | 'medium' | 'low';
}

export function AIChatWidget({
  accounts,
  activeTab,
  activeTemplate,
  financialData,
  selectedProject,
  periodType = 'monthly', // NEW: Default to monthly for backward compatibility
  hideWidget = false
}: AIChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Generate session ID for chat logging (persists across component renders)
  const sessionId = useMemo(() => {
    return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Memoize expensive calculations to prevent recalculation on every render
  const uniqueAccountsCount = useMemo(() => {
    return [...new Set(accounts.map(acc => acc.accountNumber))].length;
  }, [accounts]);

  const dateRange = useMemo(() => {
    if (!accounts.length) return 'No data';
    const dates = accounts.map(item => new Date(item.date)).sort();
    const earliest = dates[0].toLocaleDateString();
    const latest = dates[dates.length - 1].toLocaleDateString();
    return `${earliest} to ${latest}`;
  }, [accounts]);

  const periodCount = useMemo(() => {
    return [...new Set(accounts.map(acc => acc.month))].length;
  }, [accounts]);

  const uncategorizedCount = useMemo(() => {
    return [...new Set(accounts
      .filter(acc => !acc.category || acc.category === 'uncategorized')
      .map(acc => acc.accountNumber))].length;
  }, [accounts]);

  const hasMultiplePeriods = useMemo(() => {
    return periodCount > 1;
  }, [periodCount]);

  // Get the correct API URL based on environment
  // Get the correct API URL - always use relative path
const getApiUrl = useCallback(() => {
  return '/api'; // Simple, always use relative - Vite proxy handles the rest
}, []);

  // Test API connection on mount
  const testConnection = useCallback(async () => {
    try {
      const apiUrl = getApiUrl();
      console.log('Testing connection to:', `${apiUrl}/ai-financial-analysis`);
      
      // First try a simple GET to see if the endpoint exists
      const testResponse = await fetch(`${apiUrl}/ai-financial-analysis`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (testResponse.ok) {
        setConnectionStatus('connected');
        console.log('✅ API connection successful');
      } else {
        console.warn('⚠️ API endpoint exists but returned non-200:', testResponse.status);
        setConnectionStatus('error');
      }
    } catch (error) {
      console.error('❌ API connection failed:', error);
      setConnectionStatus('error');
    }
  }, [getApiUrl]);

  // Test connection when widget opens
  useEffect(() => {
    if (isOpen && connectionStatus === 'unknown') {
      testConnection();
    }
  }, [isOpen, connectionStatus, testConnection]);

  // Memoize contextual suggestions to prevent recalculation
  const contextualSuggestions = useMemo(() => {
    if (!selectedProject || accounts.length === 0) {
      return [
        'What should I look for in a quality of earnings analysis?',
        'What are common red flags in financial due diligence?',
        'How do I assess revenue quality and sustainability?'
      ];
    }

    const suggestions = {
      'trial-balance': [
        `Analyze the ${uniqueAccountsCount} accounts for quality of earnings indicators`,
        hasMultiplePeriods ? 'Identify trends and seasonal patterns in the account balances' : 'Review account balances for unusual items or potential adjustments',
        uncategorizedCount > 0 ? `Help categorize ${uncategorizedCount} uncategorized accounts for better reporting` : 'Review account classifications for financial reporting accuracy'
      ],
      'chart': [
        'Assess the chart of accounts structure for financial reporting completeness',
        'Identify missing accounts or categories that could affect due diligence',
        'Review account mappings for accurate financial statement preparation'
      ],
      'categories': [
        'Optimize category structure for better quality of earnings analysis',
        'Ensure proper classification for transaction advisory reporting',
        'Review categorization for compliance with accounting standards'
      ],
      'pl-detail': [
        'Analyze revenue quality and sustainability trends',
        'Identify one-time items and calculate normalized earnings',
        'Assess gross margin trends and operating leverage'
      ],
      'bs-detail': [
        'Evaluate working capital efficiency and cash conversion',
        'Analyze balance sheet strength and liquidity position',
        'Review asset quality and potential valuation adjustments'
      ],
      'cf-detail': [
        'Assess cash flow quality relative to reported earnings',
        'Analyze operating cash flow sustainability and drivers',
        'Review capital expenditure requirements and free cash flow'
      ],
      'nwc-detail': [
        'Analyze working capital trends and seasonal patterns',
        'Assess cash conversion cycle efficiency',
        'Identify working capital optimization opportunities'
      ],
      'ratios': [
        'Interpret key financial ratios from a transaction perspective',
        'Compare ratios to industry benchmarks and transaction multiples',
        'Identify ratio trends that could impact valuation'
      ]
    };

    return suggestions[activeTab as keyof typeof suggestions] || [
      'Provide a quality of earnings overview for this business',
      'What trends do you see that would interest potential acquirers?',
      'Identify areas requiring further due diligence investigation'
    ];
  }, [selectedProject, accounts.length, activeTab, uniqueAccountsCount, hasMultiplePeriods, uncategorizedCount]);

  // Memoize tab context suggestions
  const tabContextSuggestions = useMemo((): TabContextSuggestion[] => {
    if (!selectedProject) return [];

    const contextSuggestions: Record<string, TabContextSuggestion[]> = {
      'trial-balance': [
        {
          icon: AlertCircle,
          title: 'Quality Review',
          description: uncategorizedCount > 0 
            ? `${uncategorizedCount} accounts need attention for reporting quality`
            : `All accounts are properly categorized - quality looks good`,
          query: `Review the trial balance for ${selectedProject.name} and identify any accounts that could indicate quality of earnings issues or require further investigation from a due diligence perspective.`,
          priority: 'medium'
        },
        {
          icon: TrendingUp,
          title: 'Balance Analysis',
          description: hasMultiplePeriods ? 'Analyze trends across periods' : 'Review account balances for anomalies',
          query: hasMultiplePeriods 
            ? `Analyze the trial balance trends for ${selectedProject.name} across ${periodCount} periods and identify any patterns that could impact earnings quality or valuation.`
            : `Review the trial balance for ${selectedProject.name} and identify any unusual balances or items that would require adjustment in a transaction.`,
          priority: 'medium'
        }
      ],
      'pl-detail': [
        {
          icon: DollarSign,
          title: 'Revenue Quality',
          description: 'Assess revenue sustainability and recognition',
          query: `Analyze the revenue trends and quality for ${selectedProject.name}. What does this tell us about the sustainability and quality of earnings from a transaction advisory perspective?`,
          priority: 'medium'
        },
        {
          icon: BarChart3,
          title: 'Earnings Normalization',
          description: 'Identify one-time items and normalized EBITDA',
          query: `Review the P&L for ${selectedProject.name} and identify any one-time items, non-recurring expenses, or adjustments needed to calculate normalized EBITDA for transaction purposes.`,
          priority: 'medium'
        }
      ],
      'bs-detail': [
        {
          icon: TrendingUp,
          title: 'Working Capital Analysis',
          description: 'Assess efficiency and cash conversion',
          query: `Analyze the balance sheet for ${selectedProject.name} with focus on working capital efficiency, cash conversion, and any items that could impact transaction value.`,
          priority: 'medium'
        }
      ],
      'cf-detail': [
        {
          icon: DollarSign,
          title: 'Cash Quality Assessment',
          description: 'Evaluate cash flow vs earnings quality',
          query: `Analyze the cash flow statement for ${selectedProject.name} and assess the quality of cash conversion relative to reported earnings. What does this tell us about earnings quality?`,
          priority: 'medium'
        }
      ],
      'chart': [
        {
          icon: Lightbulb,
          title: 'Reporting Structure',
          description: 'Optimize for transaction reporting',
          query: `Review the chart of accounts structure for ${selectedProject.name} and suggest improvements for better financial reporting and due diligence preparation.`,
          priority: 'medium'
        }
      ]
    };

    return contextSuggestions[activeTab] || [];
  }, [selectedProject, activeTab, uncategorizedCount, hasMultiplePeriods, periodCount]);

  // Memoize welcome message
  const welcomeMessage = useMemo(() => {
    if (!selectedProject) {
      return "Hi! I'm your AI transaction advisory analyst. Please select a project to begin financial analysis.";
    }

    if (accounts.length === 0) {
      return `I'm ready to help analyze ${selectedProject.name}. Upload some financial data and I'll provide quality of earnings insights and due diligence analysis.`;
    }

    const contextMessages = {
      'trial-balance': `I can see you're viewing the trial balance for ${selectedProject.name} with ${uniqueAccountsCount} accounts across ${periodCount} periods. I can help identify unusual balances, analyze account patterns, or suggest improvements for financial reporting quality.`,
      'chart': `Looking at the chart of accounts for ${selectedProject.name}? I can help optimize categorizations, identify missing accounts, or assess the completeness of your financial reporting structure from a due diligence perspective.`,
      'categories': `Perfect! I can help refine the category structure for ${selectedProject.name}, suggest better mappings for more accurate financial reporting, or identify accounts that need attention for quality of earnings analysis.`,
      'pl-detail': `Analyzing the P&L for ${selectedProject.name}? I can identify revenue quality indicators, assess earnings sustainability, explain variance drivers, or highlight areas that would concern potential acquirers.`,
      'bs-detail': `I can help interpret ${selectedProject.name}'s balance sheet from a transaction advisory perspective, analyze liquidity ratios, assess working capital efficiency, or identify potential red flags for due diligence.`,
      'cf-detail': `Looking at cash flow for ${selectedProject.name}? I can analyze the quality of cash conversion, identify cash flow patterns that impact valuation, or assess sustainability from an acquirer's viewpoint.`,
      'nwc-detail': `I can help analyze ${selectedProject.name}'s working capital efficiency, identify seasonal patterns, assess cash conversion cycles, or highlight working capital issues that would impact transaction value.`,
      'ratios': `I can interpret ${selectedProject.name}'s financial ratios from a quality of earnings perspective, benchmark against typical transaction multiples, or identify ratio trends that would concern potential buyers.`,
      'default': `I'm here to provide transaction advisory insights for ${selectedProject.name}. Ask me anything about quality of earnings, due diligence red flags, or financial analysis!`
    };

    return contextMessages[activeTab as keyof typeof contextMessages] || contextMessages.default;
  }, [selectedProject, accounts.length, activeTab, uniqueAccountsCount, periodCount]);

  // Memoize context indicator
  const contextIndicator = useMemo(() => {
    if (!selectedProject) return { color: 'gray', text: 'No project selected' };
    if (accounts.length === 0) return { color: 'yellow', text: 'No financial data' };
    
    if (tabContextSuggestions.length > 0) return { color: 'blue', text: `${tabContextSuggestions.length} suggestions` };
    return { color: 'green', text: 'Ready for analysis' };
  }, [selectedProject, accounts.length, tabContextSuggestions]);

  // Initialize with welcome message based on current context
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date(),
        suggestions: contextualSuggestions
      }]);
    }
  }, [isOpen, welcomeMessage, contextualSuggestions, messages.length]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Optimized input change handler
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  }, []);

  // Optimized suggestion click handler
  const handleSuggestionClick = useCallback((suggestion: string) => {
    setInput(suggestion);
  }, []);

  // Enhanced send message handler with better error handling
  // In AIChatWidget.tsx, update the handleSendMessage function:
  const handleSendMessage = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Get the user's authentication token
      const supabase = await initSupabase();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Failed to get user session:', sessionError);
      }

      // Enhanced context with project and financial data (period-aware)
      const enhancedContext = {
        source: 'chat-widget', // NEW: Identify as chat widget for 10K token limit
        sessionId: sessionId, // NEW: Track chat session for logging
        activeTab,
        templateName: activeTemplate?.name,
        projectName: selectedProject?.name,
        projectId: selectedProject?.id,
        projectIndustry: selectedProject?.industry,
        projectBusinessDescription: selectedProject?.business_description,
        periodType: periodType, // NEW: Include period type for AI analysis
        accountCount: uniqueAccountsCount,
        dateRange: dateRange,
        periodsAvailable: periodCount,
        availablePeriods: [...new Set(accounts.map(a => a.month))].sort(), // NEW: Specific periods for AI context
        hasMultiplePeriods: hasMultiplePeriods,
        uncategorizedAccounts: uncategorizedCount,
        periodAnalysisMode: getPeriodAnalysisMode(periodType, periodCount) // NEW: Analysis guidance
      };

      const apiUrl = getApiUrl();
      console.log('Sending request to:', `${apiUrl}/ai-financial-analysis`);

      const response = await fetch(`${apiUrl}/ai-financial-analysis`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // Pass the user's access token
          ...(session?.access_token && { 'Authorization': `Bearer ${session.access_token}` })
        },
        body: JSON.stringify({
          query: input,
          financialData: accounts,
          context: enhancedContext,
          conversationHistory: messages.slice(-6) // Last 6 messages for context
        })
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        let errorMessage = `Server error (${response.status})`;
        try {
          const errorText = await response.text();
          console.error('Error response body:', errorText);
          
          // Try to parse as JSON first
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.analysis || errorData.error || errorMessage;
          } catch {
            // If not JSON, use the text directly if it's short enough
            if (errorText.length < 200) {
              errorMessage = errorText;
            }
          }
        } catch (e) {
          console.error('Failed to read error response:', e);
        }
        throw new Error(errorMessage);
      }

      // Get the response as JSON
      const aiResponse = await response.json();
      console.log('AI Response received:', aiResponse.metadata?.provider || 'unknown provider');
      
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: aiResponse.analysis || 'I apologize, but I encountered an issue analyzing your request.',
        timestamp: new Date(),
        suggestions: aiResponse.suggestions,
        actions: aiResponse.actions
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('AI Chat Error:', error);
      
      let errorContent = 'I encountered an error processing your request.';
      
      if (error instanceof Error) {
        if (error.message.includes('fetch')) {
          errorContent = 'Unable to connect to the AI service. Please check if the server is running and try again.';
        } else if (error.message.includes('Server error (500)')) {
          errorContent = 'The AI service encountered an internal error. This might be a configuration issue. Please check the server logs and try again.';
        } else if (error.message.includes('Server error (404)')) {
          errorContent = 'The AI endpoint was not found. Please check your server configuration.';
        } else if (error.message.includes('Invalid response format')) {
          errorContent = 'Received an invalid response from the server. Please check the server logs for more details.';
        } else {
          errorContent = `Error: ${error.message}`;
        }
      }
      
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: errorContent,
        timestamp: new Date(),
        suggestions: [
          'Check if the server is running on the correct port',
          'Verify your API configuration',
          'Try again in a moment'
        ]
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, activeTab, activeTemplate?.name, selectedProject?.name, selectedProject?.id, uniqueAccountsCount, dateRange, periodCount, hasMultiplePeriods, uncategorizedCount, accounts, messages, getApiUrl]);

  // Optimized key press handler
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  }, [handleSendMessage]);

  // Simplified chat formatter - focuses on readability in small chat bubbles
  const formatChatText = (text: string, isUserMessage: boolean = false): JSX.Element => {
    // Split text into lines and process each line
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Handle markdown headers (# ## ### ####)
      const textColor = isUserMessage ? 'text-white' : 'text-gray-900';
      const bodyTextColor = isUserMessage ? 'text-white' : 'text-gray-700';
      
      if (line.startsWith('# ')) {
        // H1 heading - larger for main headers
        elements.push(<h1 key={i} className={`text-base font-bold ${textColor} mb-2 mt-3`}>{line.replace('# ', '')}</h1>);
      } else if (line.startsWith('## ')) {
        // H2 heading
        elements.push(<h2 key={i} className={`text-sm font-semibold ${textColor} mb-2 mt-3`}>{line.replace('## ', '')}</h2>);
      } else if (line.startsWith('### ')) {
        // H3 heading
        elements.push(<h3 key={i} className={`text-sm font-semibold ${textColor} mb-1 mt-2`}>{line.replace('### ', '')}</h3>);
      } else if (line.startsWith('#### ')) {
        // H4 heading
        elements.push(<h4 key={i} className={`text-sm font-medium ${textColor} mb-1 mt-2`}>{line.replace('#### ', '')}</h4>);
      } else if (line.trim().startsWith('**') && line.trim().endsWith('**') && line.trim().length > 4) {
        // Standalone bold line (like "**Gross Margin Performance Summary**")
        const headerText = line.trim().replace(/^\*\*/, '').replace(/\*\*$/, '');
        elements.push(<div key={i} className={`font-semibold ${textColor} text-sm mb-2 mt-3`}>{headerText}</div>);
      } else if (line.startsWith('- ') || /^\d+\.\s/.test(line)) {
        // List items (bullets or numbers)
        const listItems = [line];
        let j = i + 1;
        while (j < lines.length && (lines[j].startsWith('- ') || /^\d+\.\s/.test(lines[j]))) {
          listItems.push(lines[j]);
          j++;
        }
        elements.push(
          <div key={i} className={`mb-2 text-sm ${bodyTextColor}`}>
            {listItems.map((item, idx) => (
              <div key={idx} className="mb-1">{formatInlineText(item, isUserMessage)}</div>
            ))}
          </div>
        );
        i = j - 1; // Skip the processed list items
      } else if (line.trim() === '') {
        // Empty line - minimal spacing for chat
        elements.push(<div key={i} className="mb-1"></div>);
      } else if (line.trim() !== '') {
        // Regular paragraph
        elements.push(<div key={i} className={`mb-2 text-sm ${bodyTextColor} leading-relaxed`}>{formatInlineText(line, isUserMessage)}</div>);
      }
    }
    
    return <div className="max-w-none">{elements}</div>;
  };

  // Format inline text (bold, italic, etc.) - simplified for chat
  const formatInlineText = (text: string, isUserMessage: boolean = false): React.ReactNode => {
    // Simple bold formatting **text**
    const boldRegex = /\*\*(.*?)\*\*/g;
    const parts = text.split(boldRegex);
    const boldTextColor = isUserMessage ? 'text-white' : 'text-gray-900';
    
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        // This is a bold part
        return <strong key={index} className={`font-medium ${boldTextColor}`}>{part}</strong>;
      }
      return part;
    });
  };

  // Hide widget if hideWidget prop is true
  if (hideWidget) {
    return null;
  }

  return (
    <>
      {/* Floating Trigger Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-all duration-200 group relative"
          >
            <Bot className="h-6 w-6" />
            
            {/* Connection status indicator */}
            <div className={`absolute -top-1 -left-1 h-3 w-3 rounded-full ${
              connectionStatus === 'connected' ? 'bg-green-500' :
              connectionStatus === 'error' ? 'bg-red-500' :
              'bg-yellow-500'
            }`}></div>
            
            {/* Context indicator badge */}
            {tabContextSuggestions.length > 0 && (
              <div className={`absolute -top-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold ${
                contextIndicator.color === 'yellow' ? 'bg-yellow-500 text-black' :
                contextIndicator.color === 'blue' ? 'bg-blue-500 text-white' :
                'bg-green-500 text-white'
              }`}>
                {tabContextSuggestions.length}
              </div>
            )}
          </button>
          
          {/* Enhanced context tooltip */}
          <div className="absolute bottom-full right-0 mb-2 bg-black text-white text-xs rounded px-3 py-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity max-w-xs">
            <div className="font-semibold">{contextIndicator.text}</div>
            {selectedProject && (
              <div className="text-gray-300">{selectedProject.name} • {activeTab}</div>
            )}
            <div className="text-gray-400">
              Status: {connectionStatus === 'connected' ? '🟢 Connected' : 
                      connectionStatus === 'error' ? '🔴 Error' : '🟡 Testing'}
            </div>
          </div>
        </div>
      )}

      {/* Chat Widget */}
      {isOpen && (
        <div className={`fixed bottom-6 right-6 bg-white rounded-lg shadow-xl border border-gray-200 z-50 transition-all duration-200 ${
          isMinimized ? 'w-80 h-16' : 'w-96 h-[650px]'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-blue-600" />
              <div>
                <div className="font-semibold text-gray-900">QoE AI Chat</div>
                {selectedProject && !isMinimized && (
                  <div className="text-xs text-gray-500 space-y-1">
                    <div className="flex items-center gap-2">
                      <span>{selectedProject.name} • {activeTab.replace('-', ' ')} • {getPeriodDisplayText(periodType)} • {uniqueAccountsCount} accounts</span>
                      <div className={`h-2 w-2 rounded-full ${
                        connectionStatus === 'connected' ? 'bg-green-500' :
                        connectionStatus === 'error' ? 'bg-red-500' :
                        'bg-yellow-500'
                      }`}></div>
                    </div>
                    {(selectedProject.industry || selectedProject.business_description) && (
                      <div className="text-blue-600 text-xs bg-blue-50 px-2 py-1 rounded">
                        💼 Context: {selectedProject.industry || 'Industry not specified'} 
                        {selectedProject.business_description && ` • ${selectedProject.business_description.length > 30 ? selectedProject.business_description.substring(0, 30) + '...' : selectedProject.business_description}`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Enhanced Contextual Suggestions Banner */}
              {tabContextSuggestions.length > 0 && (
                <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                  <div className="text-xs font-medium text-blue-900 mb-2">
                    💡 AI Insights for {activeTab.replace('-', ' ')}:
                  </div>
                  <div className="space-y-1">
                    {tabContextSuggestions.slice(0, 2).map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion.query)}
                        className="w-full flex items-start gap-2 p-2 text-left text-xs bg-white border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                      >
                        <suggestion.icon className="h-3 w-3 mt-0.5 flex-shrink-0 text-blue-600" />
                        <div>
                          <div className="font-medium text-blue-900">{suggestion.title}</div>
                          <div className="text-blue-700">{suggestion.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Connection Status Warning */}
              {connectionStatus === 'error' && (
                <div className="p-3 bg-red-50 border-b border-red-200">
                  <div className="text-xs text-red-800">
                    ⚠️ AI service connection error. Check server status and configuration.
                  </div>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ 
                height: connectionStatus === 'error' ? '300px' : tabContextSuggestions.length > 0 ? '350px' : '450px' 
              }}>
                {messages.map((message, index) => (
                  <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] p-3 rounded-lg ${
                      message.role === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      {formatChatText(message.content, message.role === 'user')}
                      
                      {/* Suggestions */}
                      {message.suggestions && message.suggestions.length > 0 && (
                        <div className="mt-3 space-y-1">
                          <div className="text-xs opacity-75 font-medium">Follow-up questions:</div>
                          {message.suggestions.slice(0, 3).map((suggestion, i) => (
                            <button
                              key={i}
                              onClick={() => handleSuggestionClick(suggestion)}
                              className="block w-full text-left text-xs bg-black bg-opacity-10 hover:bg-opacity-20 px-2 py-1 rounded transition-colors"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Action Buttons */}
                      {message.actions && message.actions.length > 0 && (
                        <div className="mt-3 space-y-1">
                          {message.actions.map((action, i) => (
                            <button
                              key={i}
                              className="block w-full text-left text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded transition-colors"
                            >
                              {action.label}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <div className="text-xs opacity-50 mt-2">
                        {message.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full"></div>
                        Analyzing financial data...
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Enhanced Input */}
              <div className="p-3 border-t border-gray-200">
                <div className="flex gap-2">
                  <input
                    value={input}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder={selectedProject ? 
                      `Ask about ${selectedProject.name}'s financial quality...` : 
                      'Select a project to begin analysis...'
                    }
                    className="flex-1 p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading || !selectedProject || connectionStatus === 'error'}
                  />
                  <button 
                    onClick={handleSendMessage}
                    disabled={isLoading || !input.trim() || !selectedProject || connectionStatus === 'error'}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Connection status indicator */}
                {connectionStatus === 'error' && (
                  <div className="text-xs text-red-600 mt-1">
                    ❌ Connection error - check server
                  </div>
                )}
                
                {connectionStatus === 'unknown' && (
                  <div className="text-xs text-yellow-600 mt-1">
                    🔄 Testing connection...
                  </div>
                )}
                
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

// Period-aware helper functions
function getPeriodDisplayText(periodType: 'monthly' | 'quarterly' | 'annual'): string {
  const displayTexts = {
    monthly: 'Monthly',
    quarterly: 'Quarterly', 
    annual: 'Annual'
  };
  return displayTexts[periodType];
}

function getPeriodAnalysisMode(periodType: 'monthly' | 'quarterly' | 'annual', periodCount: number): string {
  const analysisGuidance = {
    monthly: periodCount >= 12 ? 'focus-on-trends-and-seasonality' : 'focus-on-recent-months',
    quarterly: periodCount >= 4 ? 'focus-on-quarterly-cycles' : 'focus-on-available-quarters',
    annual: periodCount >= 3 ? 'focus-on-multi-year-trends' : 'focus-on-annual-performance'
  };
  
  return analysisGuidance[periodType];
}